import React, { useMemo } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { FilterOption, GroupOption } from '@/hooks/api/useGetAssetFilters';
import { SeedFilter } from '@/hooks/useSeeds';
import {
  getFilterDropdownWidth,
  shouldShowFilterField,
} from '@/sections/seeds/config';
import { SeedType } from '@/sections/seeds/types';
import { useAuth } from '@/state/auth';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { omit } from '@/utils/lodash.util';

interface SeedsFiltersProps {
  filters: SeedFilter;
  setFilters: React.Dispatch<React.SetStateAction<SeedFilter>>;
  seedType?: SeedType;
}

/**
 * Seeds filter bar component with advanced filters
 *
 * Note: Renders only filter dropdowns. Used inside TableControlsContainer.
 * SearchBar is passed to TableControlsContainer's filtersRight prop.
 *
 * **Performance Optimized**: React.memo prevents re-renders when props unchanged
 */
export const SeedsFilters: React.FC<SeedsFiltersProps> = React.memo(
  ({ filters, setFilters, seedType }) => {
    const {
      filterOptions,
      sortedOptions,
      status: filterOptionsStatus,
    } = useEntityFilters('Seed', 'seed-table');
    const { isPraetorianUser, isDemoOrg } = useAuth();

    // Filter options based on the seed type using centralized configuration
    // Domain tab filters: status, source, email, registrar, registrant
    // IP/CIDR tab filters: status, source, country, registrar, registrant
    // WebApplication tab filters: status, source
    const filteredSortedOptions = useMemo(() => {
      if (!seedType) return sortedOptions;

      return sortedOptions.filter(option => {
        const fieldName = option.value as string;
        return shouldShowFilterField(fieldName, seedType);
      });
    }, [sortedOptions, seedType]);

    const isDropdownFiltersApplied = useMemo(() => {
      return (
        filters.advancedFilters &&
        Object.values(filters.advancedFilters).find(value =>
          Boolean(value?.length)
        )
      );
    }, [filters]);

    return (
      <>
        {filteredSortedOptions.map((option, index) => {
          const key = option.value as string;
          const filterOption =
            filterOptions &&
            typeof filterOptions === 'object' &&
            !Array.isArray(filterOptions)
              ? filterOptions[key]
              : null;
          if (!filterOption) return null;

          return (
            <FilterDropdown
              key={index}
              id={key}
              isLoading={filterOptionsStatus === 'pending'}
              label={option.label}
              options={filterOption as (FilterOption | GroupOption)[]}
              isPraetorianUser={isPraetorianUser && !isDemoOrg}
              selectedValues={
                filters.advancedFilters?.[
                  key as keyof typeof filters.advancedFilters
                ] || []
              }
              onApply={selected => {
                setFilters(prev => ({
                  ...prev,
                  advancedFilters:
                    selected.length > 0
                      ? {
                          ...prev.advancedFilters,
                          [key]: selected.map(
                            option => option.value?.toString() || ''
                          ),
                        }
                      : omit(prev.advancedFilters, key),
                }));
              }}
              onClear={() => {
                setFilters(prev => ({
                  ...prev,
                  advancedFilters: omit(prev.advancedFilters, key),
                }));
              }}
              isLoadingOptions={filterOptionsStatus === 'pending'}
              width={getFilterDropdownWidth(key)}
            />
          );
        })}
        {isDropdownFiltersApplied && (
          <ClearFiltersButton
            onClick={() =>
              setFilters(filters => {
                return {
                  ...filters,
                  advancedFilters: {},
                };
              })
            }
          />
        )}
      </>
    );
  }
);

SeedsFilters.displayName = 'SeedsFilters';
